import { CoreLmsIcon, CoreRadioGroup } from "../../core";
import { CoreRadioGroupProps, RadioOption } from "../../core/radio/RadioGroup";
import InstitutionBaseRow, { EditConsumerProps, ViewConsumerProps } from "./InstitutionBaseRow";
import { getDisabledProps, useInstitutionRowHeader } from "./informationHelper";
import { useMemo } from "react";
import type { Lms } from "@verity/api-client/types";
import type { InstitutionType } from "@verity/database/prisma";
import styles from "./InstitutionBaseRow.module.css";

type InstitutionRadioGroupProps = Pick<
	CoreRadioGroupProps,
	"radioOptions" | "selectedValue" | "disabled" | "disabledTooltip" | "radioGroupAriaLabelledby"
> & {
	onChange: (value?: string) => void;
};

function InstitutionRadioGroup({ onChange, ...props }: InstitutionRadioGroupProps) {
	return (
		<CoreRadioGroup
			{...props}
			onChange={(_, value) => onChange(value)}
			formControlLabelProps={{ sx: { gap: "5px", marginLeft: "0px" } }}
		/>
	);
}

function LmsRadioLabel({ children }: { children: React.ReactNode }) {
	return <span className={styles.platformLabelText}>{children}</span>;
}

function LmsRadioWrapper({ children }: { children: React.ReactNode }) {
	return <div className={styles.platformLabel}>{children}</div>;
}

type EditLmsRowProps = EditConsumerProps & {
	lms?: Lms;
	onChange: (value?: Lms) => void;
	product: InstitutionType;
};
type ViewLmsRowProps = ViewConsumerProps & {
	lms?: Lms;
};

type InstitutionLmsRowProps = EditLmsRowProps | ViewLmsRowProps;

export function InstitutionLmsRow({ lms, ...props }: InstitutionLmsRowProps) {
	const { t, commonProps } = useInstitutionRowHeader("platform", props);

	const platformRadioOptions = useMemo((): RadioOption<Lms | "none">[] => {
		return [
			{
				value: "none",
				label: <LmsRadioLabel>{t("none")}</LmsRadioLabel>,
			},
			{
				value: "canvas",
				label: (
					<LmsRadioWrapper>
						<CoreLmsIcon lms={"canvas"} width={40} height={30} />
						<LmsRadioLabel>{t("canvas")}</LmsRadioLabel>
					</LmsRadioWrapper>
				),
			},
			{
				value: "moodle",
				label: (
					<LmsRadioWrapper>
						<CoreLmsIcon lms={"moodle"} width={40} height={30} />
						<LmsRadioLabel>{t("moodle")}</LmsRadioLabel>
					</LmsRadioWrapper>
				),
			},
			{
				value: "BlackboardLearn",
				label: (
					<LmsRadioWrapper>
						<CoreLmsIcon lms={"BlackboardLearn"} width={50} height={30} />
						<LmsRadioLabel>{t("BlackboardLearn")}</LmsRadioLabel>
					</LmsRadioWrapper>
				),
			},
			{
				value: "desire2learn",
				label: (
					<LmsRadioWrapper>
						<CoreLmsIcon lms={"desire2learn"} width={40} height={30} />
						<LmsRadioLabel>{t("desire2learn")}</LmsRadioLabel>
					</LmsRadioWrapper>
				),
			},
		];
	}, [t]);

	if (props.mode === "edit") {
		const isCandidate = props.product === "candidate";
		return (
			<InstitutionBaseRow
				{...commonProps}
				strategy={{
					mode: "edit",
					createControl: (id) => (
						<InstitutionRadioGroup
							{...getDisabledProps(props, isCandidate, isCandidate ? t("disabled") : undefined)}
							radioOptions={platformRadioOptions}
							onChange={(value) => props.onChange(value === "none" ? undefined : (value as Lms))}
							selectedValue={lms === undefined ? "none" : lms}
							radioGroupAriaLabelledby={id}
						/>
					),
				}}
			/>
		);
	}

	return (
		<InstitutionBaseRow
			{...commonProps}
			strategy={{ mode: "view", value: lms ? t(lms) : t("none"), copyAriaLabel: t("copyAriaLabel") }}
		/>
	);
}
